import { RouteProp, useNavigation, useRoute } from '../../navigation/native';
import React from 'react';
import { useDispatch } from 'react-redux';

import { RootStackParamList } from '../../app/Navigation';
import { Route } from '../../app/Route';
import { StandingOrderAction, TaskAction } from '../../services/dataModels';
import { selectEditedSnippets, shiftLogActions } from '../../store';
import { AppDispatch } from '../../store/store';
import { Layout, Link } from '../editing';

const ActionListScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { params } = useRoute<RouteProp<RootStackParamList, 'EditingRowScreen'>>();
  const navigation = useNavigation();

  const snippet = selectEditedSnippets.useHook();
  const rowData = snippet.data[params.rollupIndex ?? 0].rowData[params.rowIndex];

  let actions: {
    label: StandingOrderAction | TaskAction;
    predicate: () => void;
    action: () => void;
  }[];

  switch (snippet.type) {
    case 'StandingOrder':
      actions = [
        {
          label: 'Activate',
          predicate: () => {
            const status = rowData.statusDisplayName;
            return status === 'Planned' || status === 'Approved';
          },
          action: () => {
            dispatch(
              shiftLogActions.activateStandingOrder({
                displayTemplateId: snippet.displayTemplateId,
                standingOrderId: rowData.standingOrderId as string,
              }),
            )
              .unwrap()
              .then(() => {
                navigation.navigate(Route.editingRowScreen);
              })
              .catch(() => {});
          },
        },
        {
          label: 'Complete',
          predicate: () => {
            const status = rowData.statusDisplayName;
            return status === 'Activated';
          },
          action: () => {
            dispatch(
              shiftLogActions.completeStandingOrder({
                displayTemplateId: snippet.displayTemplateId,
                standingOrderId: rowData.standingOrderId as string,
              }),
            )
              .unwrap()
              .then(() => {
                navigation.navigate(Route.editingRowScreen);
              })
              .catch(() => {});
          },
        },
      ];
      break;
    case 'Task':
      actions = [
        {
          label: 'Start',
          predicate: () => {
            const status = rowData.state;
            return status === 'Planned';
          },
          action: () => {
            navigation.navigate(Route.actionScreen, { ...params, action: 'Start' });
          },
        },
        {
          label: 'Pause',
          predicate: () => {
            const status = rowData.state;
            return status === 'In-Progress';
          },
          action: () => {
            navigation.navigate(Route.actionScreen, { ...params, action: 'Pause' });
          },
        },
        {
          label: 'Resume',
          predicate: () => {
            const status = rowData.state;
            return status === 'Paused';
          },
          action: () => {
            navigation.navigate(Route.actionScreen, { ...params, action: 'Resume' });
          },
        },
        {
          label: 'Complete',
          predicate: () => {
            const status = rowData.state;
            return status === 'Planned' || status === 'In-Progress';
          },
          action: () => {
            navigation.navigate(Route.actionScreen, { ...params, action: 'Complete' });
          },
        },
        {
          label: 'Cancel',
          predicate: () => {
            const status = rowData.state;
            return status === 'Planned' || status === 'In-Progress' || status === 'Paused';
          },
          action: () => {
            navigation.navigate(Route.actionScreen, { ...params, action: 'Cancel' });
          },
        },
      ];
      break;
    default:
      break;
  }

  return (
    <Layout
      header={<></>}
      title="Select Action"
      content={
        <>
          {actions.map(
            (action, index) =>
              action.predicate() && (
                <Link key={index} name={action.label} handlePress={() => action.action()} />
              ),
          )}
        </>
      }
    />
  );
};

export default ActionListScreen;
